import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Collapse, Table, Input, Button } from "reactstrap";
import { getReviewCategories } from "../../redux/actions/indexActions";
import { BsChevronDown, BsChevronRight } from "react-icons/bs";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { Link } from "react-router-dom";
import { MdAdd } from "react-icons/md";
import axios from "axios";
import { toast } from "react-toastify";

const ColorSwatch = ({ color }) => (
  <span className="d-inline-flex align-items-center gap-2">
    {color ? (
      <>
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 4,
            backgroundColor: color,
            border: "1px solid #dee2e6",
            display: "inline-block",
          }}
        />
        <span>{color}</span>
      </>
    ) : (
      "-"
    )}
  </span>
);

const StatusSelect = ({ value, onChange, size = "sm" }) => (
  <Input
    type="select"
    size={size}
    value={Number(value) === 1 ? "1" : "0"}
    onChange={(e) => onChange(e.target.value)}
    onClick={(e) => e.stopPropagation()}
  >
    <option value="1">Active</option>
    <option value="0">Inactive</option>
  </Input>
);

const ReviewCategoryIndex = () => {
  const dispatch = useDispatch();
  const {
    AuthReducer: { auth },
    indexReducer: { questionReviewCategories },
  } = useSelector((state) => state);
  const [expandedIds, setExpandedIds] = useState({});

  const refreshList = () => dispatch(getReviewCategories(auth?.token));

  useEffect(() => {
    refreshList();
  }, [auth, dispatch]);

  const toggleRow = (id) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await axios.put(
        `/api/admin/v1/review-category-status/${id}`,
        { status: Number(status) },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      toast.success(res.data?.message || "Status updated successfully");
      refreshList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    }
  };

  const handleDelete = (id, label) => {
    if (!window.confirm(`Are you sure you want to delete this ${label}?`)) return;
    axios
      .delete(`/api/admin/v1/review-category-delete/${id}`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
      })
      .then((res) => {
        toast.success(res.data?.message || "Deleted successfully");
        refreshList();
      })
      .catch((err) =>
        toast.error(err.response?.data?.message || "Something went wrong")
      );
  };

  const renderActions = (row, label) => (
    <div
      className="d-flex align-items-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Link to={`/add-review-category/${row.id}`}>
        <AiOutlineEdit size={22} className="text-warning me-3" />
      </Link>
      <AiOutlineDelete
        size={22}
        className="text-danger"
        style={{ cursor: "pointer" }}
        onClick={() => handleDelete(row.id, label)}
      />
    </div>
  );

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">Review Category</h5>
            <Link to="/add-review-category">
              <Button className="bg-white text-black" size="sm">
                <span className="me-2">
                  <MdAdd size={22} />
                </span>
                Add Review Category
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <div
        className="bg-white px-3 py-4 rounded-2 my-4"
        style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
      >
        <div className="col-md-12">
          <Table hover responsive>
            <thead>
              <tr>
                <th style={{ width: 40 }} />
                <th>SL. No.</th>
                <th>Name</th>
                <th>Type</th>
                <th>Description</th>
                <th>Color</th>
                <th>Child Categories</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!questionReviewCategories?.length ? (
                <tr>
                  <td colSpan="9">No data found</td>
                </tr>
              ) : (
                questionReviewCategories.map((item, index) => {
                  const isExpanded = !!expandedIds[item.id];
                  const childCount = item?.childCategories?.length || 0;
                  return (
                    <React.Fragment key={item.id}>
                      <tr
                        role="button"
                        onClick={() => toggleRow(item.id)}
                        className={isExpanded ? "table-active" : ""}
                      >
                        <td>
                          {childCount > 0 &&
                            (isExpanded ? (
                              <BsChevronDown size={18} />
                            ) : (
                              <BsChevronRight size={18} />
                            ))}
                        </td>
                        <th scope="row">{index + 1}</th>
                        <td>{item?.name}</td>
                        <td className="text-capitalize">{item?.type}</td>
                        <td>{item?.description || "-"}</td>
                        <td>
                          <ColorSwatch color={item?.category_color} />
                        </td>
                        <td>{childCount}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <StatusSelect
                            value={item.status}
                            onChange={(value) =>
                              handleStatusChange(item.id, value)
                            }
                          />
                        </td>
                        <td>{renderActions(item, "question")}</td>
                      </tr>
                      <tr>
                        <td colSpan="9" className="p-0 border-0">
                          <Collapse isOpen={isExpanded}>
                            <div className="p-3 bg-light">
                              <h6 className="mb-3">Child Categories</h6>
                              {childCount === 0 ? (
                                <p className="mb-0 text-muted">
                                  No child categories found.
                                </p>
                              ) : (
                                <Table
                                  size="sm"
                                  bordered
                                  responsive
                                  className="mb-0 bg-white"
                                >
                                  <thead>
                                    <tr>
                                      <th>SL. No.</th>
                                      <th>Name</th>
                                      <th>Color</th>
                                      <th>Status</th>
                                      <th>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.childCategories.map(
                                      (child, childIndex) => (
                                        <tr key={child.id}>
                                          <th scope="row">{childIndex + 1}</th>
                                          <td>{child.name}</td>
                                          <td>
                                            <ColorSwatch
                                              color={child.category_color}
                                            />
                                          </td>
                                          <td>
                                            <StatusSelect
                                              value={child.status}
                                              onChange={(value) =>
                                                handleStatusChange(
                                                  child.id,
                                                  value
                                                )
                                              }
                                            />
                                          </td>
                                          <td>
                                            {renderActions(
                                              child,
                                              "child category"
                                            )}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </Table>
                              )}
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default ReviewCategoryIndex;
